import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Grid, Typography, Card, CardContent, FormControl, InputLabel,
    Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, CircularProgress, Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { makeApiRequest } from '../../../utils/api';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: '16px',
    textAlign: 'center',
    fontFamily: 'Roboto',
}));

const COMPONENT_MAP = {
    'DOMAIN TECHNICAL CONCEPT': ['written', 'viva'],
    'DRAFTING': ['written', 'viva', 'project_presentation'],
    'MODELING': ['written', 'viva', 'project_presentation'],
    'ATTENDANCE': ['attendance'],
    'PROJECT (DRAFTING + MODELING)': ['project_understanding', 'explanation_technical', 'tools_skill'],
};

const maxMarksMap = {
    'domain technical concept': { written: 5, viva: 5 },
    'drafting': { written: 3, viva: 3, project_presentation: 4 },
    'modeling': { written: 3, viva: 3, project_presentation: 4 },
    'attendance': { attendance: 10 },
    'project (drafting + modeling)': { project_understanding: 3, explanation_technical: 3, tools_skill: 4 }
};

const displayNames = {
    written: 'Written Exam',
    viva: 'Viva Voce',
    project_presentation: 'Project Presentation',
    attendance: 'Attendance',
    project_understanding: 'Project Understanding',
    explanation_technical: 'Technical Explanation',
    tools_skill: 'Tools & Skills Mastery'
};

const getRubricsForGenericName = (genericName) => {
    const key = (genericName || '').toUpperCase().trim();
    const fields = COMPONENT_MAP[key] || [];
    const maxMarks = maxMarksMap[key.toLowerCase()] || {};
    return fields.map(field => ({
        name: displayNames[field] || field,
        weight: maxMarks[field] || 0
    }));
};

const LinkedAssessments = () => {
    const [searchParams] = useSearchParams();
    const courseIdParam = searchParams.get('courseId');

    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [assessments, setAssessments] = useState([]);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [loadingAssessments, setLoadingAssessments] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCourses = async () => {
            setLoadingCourses(true);
            setError(null);
            try {
                const response = await makeApiRequest('post', '/api/course/list', {
                    token: localStorage.getItem('authToken'),
                });
                if (response && response.status === 'success' && response.data) {
                    setCourses(response.data);
                    
                    // If a courseId query parameter is present, select it
                    if (courseIdParam) {
                        const parsedId = Number(courseIdParam);
                        const match = response.data.find(c => Number(c.id) === parsedId);
                        if (match) {
                            setSelectedCourse(parsedId);
                            loadAssessmentsForCourse(parsedId);
                        }
                    }
                } else {
                    throw new Error(response?.message || 'Failed to fetch courses');
                }
            } catch (err) {
                console.error('Error fetching courses:', err);
                setError('Failed to load courses list.');
            } finally {
                setLoadingCourses(false);
            }
        };
        fetchCourses();
    }, [courseIdParam]);

    const loadAssessmentsForCourse = async (courseId) => {
        setLoadingAssessments(true);
        setError(null);
        try {
            const response = await makeApiRequest('post', '/api/batch/assessments', {
                token: localStorage.getItem('authToken'),
                batch_id: 0,
                course_id: courseId
            });
            if (response && response.status === 'success' && response.data) {
                setAssessments(response.data);
            } else {
                throw new Error(response?.message || 'Failed to fetch assessments');
            }
        } catch (err) {
            console.error('Error fetching assessments:', err);
            setError('Failed to load linked assessments for the selected course.');
            setAssessments([]);
        } finally {
            setLoadingAssessments(false);
        }
    };

    const handleCourseChange = (event) => {
        const courseId = event.target.value;
        setSelectedCourse(courseId);
        if (!courseId) {
            setAssessments([]);
            return;
        }
        loadAssessmentsForCourse(courseId);
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#ECEDFF', p: 3, fontFamily: 'Roboto' }}>
            <Grid container justifyContent="center">
                <Grid item xs={11}>
                    <Box sx={{ height: '80px', display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h4" sx={{ color: '#481E82', fontWeight: '500' }}>
                            Linked Assessments
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <Card sx={{ mb: 3, borderRadius: '8px' }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="course-select-label">Select Course / Plan</InputLabel>
                                        <Select
                                            labelId="course-select-label"
                                            value={selectedCourse}
                                            label="Select Course / Plan"
                                            onChange={handleCourseChange}
                                            disabled={loadingCourses}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {courses.map((course) => (
                                                <MenuItem key={course.id} value={course.id}>
                                                    {course.course_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {(loadingCourses || loadingAssessments) && (
                                    <Grid item>
                                        <CircularProgress size={24} sx={{ color: '#481E82' }} />
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {selectedCourse && !loadingAssessments && (
                        <Box sx={{ mt: 3 }}>
                            {assessments.length > 0 ? (
                                <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: 3 }}>
                                    <Table aria-label="assessment rubrics table">
                                        <TableHead sx={{ backgroundColor: '#ECEDFF' }}>
                                            <TableRow>
                                                <StyledTableCell sx={{ fontWeight: 'bold', color: 'black' }}>S/No.</StyledTableCell>
                                                <StyledTableCell sx={{ fontWeight: 'bold', color: 'black', textAlign: 'left' }}>Assessment Category</StyledTableCell>
                                                <StyledTableCell sx={{ fontWeight: 'bold', color: 'black', textAlign: 'left' }}>Display Name (Specific)</StyledTableCell>
                                                <StyledTableCell sx={{ fontWeight: 'bold', color: 'black', textAlign: 'left' }}>Rubric Criteria & Weightage</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {assessments.map((item, index) => {
                                                const rubrics = getRubricsForGenericName(item.generic_name);
                                                return (
                                                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9ff' } }}>
                                                        <StyledTableCell>{index + 1}</StyledTableCell>
                                                        <StyledTableCell sx={{ textAlign: 'left', fontWeight: '500' }}>
                                                            {item.generic_name}
                                                        </StyledTableCell>
                                                        <StyledTableCell sx={{ textAlign: 'left', color: '#481E82' }}>
                                                            {item.specific_name}
                                                        </StyledTableCell>
                                                        <StyledTableCell sx={{ textAlign: 'left' }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                {rubrics.map((rubric, idx) => (
                                                                    <Typography key={idx} variant="body2" sx={{ fontSize: '13px' }}>
                                                                        • <strong>{rubric.name}</strong>: {rubric.weight} Marks
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        </StyledTableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    No assessments linked to this course.
                                </Alert>
                            )}
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default LinkedAssessments;
